import { getAccessToken, refreshSession, RefreshUnavailableError } from './session';

/** Error body shapes from the API: validation (`string[]`) or everything else (`string`). */
interface ErrorBody {
  statusCode?: number;
  message?: string | string[];
  error?: string;
  errorCode?: string;
}

export class ApiError extends Error {
  readonly status: number;
  /** Individual messages; validation errors have one per field ("field: problem"). */
  readonly messages: string[];
  readonly errorCode: string | undefined;

  constructor(status: number, messages: string[], errorCode?: string) {
    super(messages.join('\n') || `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.messages = messages;
    this.errorCode = errorCode;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Network failure or 5xx: worth retrying. */
  get isTransient(): boolean {
    return this.status === 0 || this.status >= 500;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

/** Human-readable message for toasts and inline errors. */
export function errorMessage(e: unknown): string {
  if (isApiError(e)) {
    if (e.status === 0) return 'Cannot reach the server. Check your connection and try again.';
    if (e.status === 403) return 'You do not have permission to do this.';
    return e.message;
  }
  return e instanceof Error ? e.message : 'Something went wrong';
}

export type QueryValue = string | number | boolean | readonly string[] | null | undefined;
export type Query = Record<string, QueryValue>;

/** Arrays become comma-separated (the API's list convention); empty values are dropped. */
export function buildQuery(query?: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length) params.set(key, value.join(','));
    } else {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** True while pending work is flushed as the page closes; see `duringUnload`. */
let unloading = false;

/**
 * Run `fn` so that any request it starts survives the page closing (fetch `keepalive`). Requests
 * must start synchronously inside `fn`, which they do whenever an access token is already held.
 */
export function duringUnload(fn: () => void): void {
  unloading = true;
  try {
    fn();
  } finally {
    unloading = false;
  }
}

interface RequestOptions {
  query?: Query;
  /** JSON body. */
  body?: unknown;
  /** Multipart body (file uploads). Mutually exclusive with `body`. */
  form?: FormData;
  signal?: AbortSignal;
  /** Send the bearer token and refresh on 401 (default true). */
  auth?: boolean;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: ErrorBody | null = null;
  try {
    body = (await res.json()) as ErrorBody;
  } catch {
    // Non-JSON error (proxy page, empty body).
  }
  const raw = body?.message ?? body?.error ?? res.statusText;
  const messages = (Array.isArray(raw) ? raw : [raw]).filter(
    (m): m is string => typeof m === 'string' && m.length > 0,
  );
  return new ApiError(res.status, messages, body?.errorCode);
}

async function refreshedToken(): Promise<string | null> {
  try {
    return (await refreshSession())?.accessToken ?? null;
  } catch (e) {
    if (e instanceof RefreshUnavailableError) throw new ApiError(0, [e.message]);
    throw e;
  }
}

async function send(method: string, path: string, opts: RequestOptions): Promise<Response> {
  const auth = opts.auth ?? true;
  const url = path + buildQuery(opts.query);

  const attempt = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (auth && token) headers.Authorization = `Bearer ${token}`;
    try {
      return await fetch(url, {
        method,
        headers,
        body: opts.form ?? (opts.body === undefined ? undefined : JSON.stringify(opts.body)),
        credentials: 'include',
        signal: opts.signal,
        keepalive: unloading && !opts.form,
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw new ApiError(0, ['Cannot reach the server']);
    }
  };

  let token = auth ? getAccessToken() : null;
  if (auth && !token) token = await refreshedToken();
  let res = await attempt(token);

  if (res.status === 401 && auth) {
    // Another request may already have refreshed while this one was in flight: reuse its token
    // rather than rotating the refresh cookie again.
    const latest = getAccessToken();
    const retryToken = latest && latest !== token ? latest : await refreshedToken();
    if (retryToken) res = await attempt(retryToken);
  }

  if (!res.ok) throw await parseError(res);
  return res;
}

async function json<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await send(method, path, opts);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

function filenameFrom(res: Response, fallback: string): string {
  const cd = res.headers.get('Content-Disposition') ?? '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star?.[1]) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain?.[1] ?? fallback;
}

/** Saves a Blob through a temporary <a download>. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  // Revoke later: some browsers start the download asynchronously.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) =>
    json<T>('GET', path, { query, signal }),
  post: <T>(path: string, body?: unknown, query?: Query) => json<T>('POST', path, { body, query }),
  put: <T>(path: string, body?: unknown) => json<T>('PUT', path, { body }),
  patch: <T>(path: string, body?: unknown) => json<T>('PATCH', path, { body }),
  delete: <T = void>(path: string) => json<T>('DELETE', path),
  upload: <T>(path: string, form: FormData, query?: Query) =>
    json<T>('POST', path, { form, query }),
  /** Unauthenticated JSON call (login). */
  public: <T>(method: string, path: string, body?: unknown) =>
    json<T>(method, path, { body, auth: false }),

  /** Authenticated binary fetch (media, for blob URLs). */
  async blob(path: string, signal?: AbortSignal): Promise<Blob> {
    const res = await send('GET', path, { signal });
    return res.blob();
  },

  /** Authenticated file download: fetch with the bearer header, then save as a Blob. */
  async download(path: string, query: Query | undefined, fallbackName: string): Promise<void> {
    const res = await send('GET', path, { query });
    saveBlob(await res.blob(), filenameFrom(res, fallbackName));
  },
};
